/**
 * CR307/CR307a: S0 Scan Settings — Scan Tab (sub-tabbed: Web Scan / Flyer Scan)
 * 
 * This component uses the app_scan_settings singleton for persistence via
 * useScanSettings(). It intentionally does NOT use the parent batch-run
 * config props (config, updateExecutionConfig, updateFlyerDefaults) — those
 * are accepted for interface compatibility but scan defaults now live in
 * the singleton table, not config_json.flyerExtraction.
 */

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Globe, FileText, Loader2 } from 'lucide-react';
import { WebScanTab } from './WebScanTab';
import { FlyerScanTab } from './FlyerScanTab';
import type { AppScanSettings, RecipeOption } from '@/hooks/useScanSettings';
import type { BatchRunFlyerConfig, FlyerCaptureMode } from '../types';

interface ScanSettingsTabProps {
  config: BatchRunFlyerConfig;
  updateConfig: <K extends keyof BatchRunFlyerConfig>(key: K, value: BatchRunFlyerConfig[K]) => void;
  disabled?: boolean;
  // Global settings passed from parent
  globalSettings: AppScanSettings;
  globalRecipes: RecipeOption[];
  isLoadingGlobal: boolean;
  isSavingGlobal: boolean;
  isGlobalDirty: boolean;
  updateGlobalField: <K extends keyof AppScanSettings>(key: K, value: AppScanSettings[K]) => void;
  saveGlobal: () => Promise<boolean>;
  resetGlobal: () => void;
}

export function ScanSettingsTab({
  config,
  updateConfig,
  disabled = false,
  globalSettings,
  globalRecipes,
  isLoadingGlobal,
  updateGlobalField,
}: ScanSettingsTabProps) {
  if (isLoadingGlobal) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading scan settings…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* CR307a: Clarify scope — these are global defaults, not batch-run config */}
      <p className="text-xs text-muted-foreground bg-muted/40 rounded px-3 py-2">
        These are <strong>global scan defaults</strong> — they apply to all future runs unless overridden at the merchant-source level.
      </p>

      <Tabs defaultValue="web" className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-auto">
          <TabsTrigger value="web" className="text-xs py-2">
            <Globe className="h-3 w-3 mr-1.5" />
            Web Scan
          </TabsTrigger>
          <TabsTrigger value="flyer" className="text-xs py-2">
            <FileText className="h-3 w-3 mr-1.5" />
            Flyer Scan
          </TabsTrigger>
        </TabsList>

        <TabsContent value="web" className="mt-4">
          <WebScanTab
            settings={globalSettings}
            recipes={globalRecipes}
            updateField={updateGlobalField}
            disabled={disabled}
          />
        </TabsContent>

        <TabsContent value="flyer" className="mt-4">
          <FlyerScanTab
            settings={globalSettings}
            updateField={updateGlobalField}
            disabled={disabled}
            flyerCaptureMode={config.flyerCaptureMode}
            onFlyerCaptureModeChange={(mode) => updateConfig('flyerCaptureMode', mode)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
